import { Controller } from "@hotwired/stimulus";
import Sortable from "sortablejs";
import GoogleMapsService from "../services/google_maps_service";

export default class AddressFormController extends Controller {
  static targets = [
    "locationInput",
    "cityInput",
    "stateInput",
    "contactsContainer",
    "map",
    "locationError",
    "cityError",
    "stateError",
    "contactTemplate",
    "addressList",
    "saveButton",
    "cancelButton",
    "directionsPanel",
    "content",
    "sectionTitle",
    "startingPoint"
  ];
  static values = {
    addresses: { type: Array, default: [] },
    measurementUnit: String,
    businessAddress: String,
    savedAddresses: Array,
    googleMapsApiKey: String
  };

  connect() {
    this.initObserver();
    this.initGoogleMaps();
    const startingPointSelect = this.startingPointTarget;
    const savedStartingPoint = startingPointSelect.dataset.startingPoint;
    if (savedStartingPoint) {
      startingPointSelect.value = savedStartingPoint;
    }
  }
  toggleSection(event) {
    const sectionTitle = event.currentTarget;
    const content = this.contentTargets.find(target => target.previousElementSibling === sectionTitle);
    const icon = sectionTitle.querySelector('i');

    if (content.classList.contains('hidden')) {
      content.classList.remove('hidden');
      icon.classList.remove('fa-chevron-down');
      icon.classList.add('fa-chevron-up');
    } else {
      content.classList.add('hidden');
      icon.classList.remove('fa-chevron-up');
      icon.classList.add('fa-chevron-down');
    }
  }

  // ... (keep all existing methods)
  async initGoogleMaps() {
    this.googleMapsService = new GoogleMapsService(this.googleMapsApiKeyValue);
    await this.googleMapsService.loadGoogleMapsAPI();
    this.onGoogleMapsLoaded();
  }

  onGoogleMapsLoaded() {
    this.initAutocomplete();
    this.googleMapsService.initMap(this.mapTarget);
    this.googleMapsService.initDirectionsService();
    this.initializeSavedAddresses();
    if (this.addressesValue.length > 0) {
      this.calculateAndDisplayRoute().catch(error => console.error("Error displaying route:", error));
    }
  }

  updateStartingPoint() {

    // Update the hidden field for settings
    this.updateHiddenField();

    if (this.addressesValue.length > 0) {
      this.calculateAndDisplayRoute().catch(error => console.error("Error updating route:", error));
    }
    this.updateTotalDisplay();
  }

  updateHiddenField() {
    const addressesHiddenField = document.getElementById('mover_order_addresses_attributes');
    if (addressesHiddenField) {
      addressesHiddenField.value = JSON.stringify(this.addressesValue);
    }

    const settingsField = document.getElementById('order_settings');
    console.log("Settings field value:", settingsField);
    if (settingsField) {
      const currentSettings = JSON.parse(settingsField.value || '{}');
      currentSettings.starting_point = this.startingPointTarget.value;
      settingsField.value = JSON.stringify(currentSettings);
    }
  }


  dispatchAddressUpdate() {
    console.log("Dispatching address update");
    const event = new CustomEvent("addressesUpdated", {
      detail: this.addressesValue,
      bubbles: true
    });
    this.element.dispatchEvent(event);
  }

  initObserver() {
    this.observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === "childList" && mutation.addedNodes.length > 0) {
          const tbody = this.element.querySelector("#address-list tbody");
          if (tbody && !this.sortable) {
            this.initializeSortable(tbody);
          }
        }
      });
    });

    this.observer.observe(this.element, {
      childList: true,
      subtree: true,
    });
  }

  initializeSortable(tbody) {
    this.sortable = new Sortable(tbody, {
      animation: 150,
      onEnd: this.end.bind(this),
    });
  }

  initializeSavedAddresses() {
    if (this.hasSavedAddressesValue && this.savedAddressesValue.length > 0) {
      const formattedAddresses = this.savedAddressesValue.map(address => this.formatSavedAddress(address));
      this.addressesValue = formattedAddresses;

      this.updateAddressList();
      this.calculateRoutes();
    } else {
      console.log("No saved addresses to process");
    }
  }

  centerMapOnAddress(address) {
    console.log("Centering map on address:", address);
    const fullAddress = `${address.address}, ${address.city}, ${address.state}, ${address.postalCode}`;
    this.googleMapsService.geocodeAddress(fullAddress, (results, status) => {
      if (status === 'OK' && results[0]) {
        const location = results[0].geometry.location;
        this.googleMapsService.setMapCenter(location.lat(), location.lng(), 15); // Set zoom level to 15
        this.googleMapsService.setMarkerPosition(location.lat(), location.lng());
      } else {
        console.error('Geocode was not successful for the following reason: ' + status);
      }
    });
  }

  end(event) {

    const item = this.addressesValue[event.oldIndex];
    const newValue = [
      ...this.addressesValue.slice(0, event.oldIndex),
      ...this.addressesValue.slice(event.oldIndex + 1),
    ];
    newValue.splice(event.newIndex, 0, item);
    this.addressesValue = newValue;
    this.updatePositions();

  }



  disconnect() {
    if (this.sortable) {
      this.sortable.destroy();
      this.sortable = null;
    }
    if (this.observer) {
      this.observer.disconnect();
    }
    // Remove event listener for Google Maps loading
    document.removeEventListener("google-maps-loaded", this.onGoogleMapsLoaded.bind(this));
  }


  initAutocomplete() {
    if (!this.hasLocationInputTarget) return;

    this.googleMapsService.initAutocomplete(this.locationInputTarget, (place) => {
      this.fillInAddress(place);
      this.renderAddress(place);
    });
  }

  selectAddress(event) {
    const addressElement = event.currentTarget;
    const addressId = addressElement.getAttribute("data-address-id");
    if (this.selectedAddressId === addressId) {
      return;
    }

    this.selectedAddressId = addressId;
    this.highlightSelectedAddress(addressElement);
    this.loadAddressData(addressId);

    if (this.hasSaveButtonTarget) {
      this.saveButtonTarget.textContent = "Update Address";
    }
    if (this.hasCancelButtonTarget) {
      this.cancelButtonTarget.classList.remove("hidden");
    }
  }

  cancelEdit(event) {
    event.preventDefault();
    this.clearForm();
    this.deselectAddress();
  }

  logAddressState(methodName) {
    console.log(`[${methodName}] Current addressesValue:`, JSON.parse(JSON.stringify(this.addressesValue)));
  }



  highlightSelectedAddress(addressElement) {
    if (this.hasAddressListTarget) {
      this.addressListTarget.querySelectorAll("tr").forEach((tr) => {
        tr.classList.remove("bg-orange-200");
      });

      addressElement.classList.add("bg-orange-200");
    }

    if (this.hasSaveButtonTarget) {
      this.saveButtonTarget.textContent = "Update Address";
    }
  }


  loadAddressData(addressId) {
    const address = this.addressesValue.find((a) => a.id == addressId);
    if (address) {
      this.populateForm(address);
    } else {
      console.error(`Address with id ${addressId} not found`);
    }

  }

  loadContacts(contacts) {
    if (this.hasContactsContainerTarget) {
      this.contactsContainerTarget.innerHTML = "";
      contacts.forEach((contact) => {
        const contactTemplate = this.contactTemplateTarget.content.cloneNode(true);
        contactTemplate.querySelector(".contact").setAttribute("data-contact-id", contact.id);
        contactTemplate.querySelector("input[name='contact_id']").value = contact.id;
        contactTemplate.querySelector("input[placeholder='Contact Name']").value = contact.name;
        contactTemplate.querySelector("input[placeholder='Contact Phone']").value = contact.phone;
        this.contactsContainerTarget.appendChild(contactTemplate);
      });
    }
  }


  getContacts() {
    return Array.from(this.contactsContainerTarget.querySelectorAll(".contact")).map((contactElement) => ({
      id: contactElement.getAttribute("data-contact-id"),
      name: contactElement.querySelector("input[placeholder='Contact Name']").value,
      phone: contactElement.querySelector("input[placeholder='Contact Phone']").value,
    }));
  }

  fillInAddress(place) {
    const addressComponents = place.address_components;
    const addressTypeMapping = {
      street_number: "short_name",
      route: "long_name",
      locality: "long_name",
      sublocality_level_1: "short_name",
      administrative_area_level_1: "short_name",
      postal_code: "short_name",
      country: "long_name",
    };

    let streetAddress = "";

    addressComponents.forEach((component) => {
      const addressType = component.types[0];

      if (addressType in addressTypeMapping) {
        const val = component[addressTypeMapping[addressType]];

        if (addressType === "street_number" || addressType === "route") {
          streetAddress += val + " ";
        } else {
          let inputId = addressType;
          if (addressType === "administrative_area_level_1") {
            inputId = "state";
          } else if (addressType === "locality") {
            inputId = "city";
          } else if (addressType === "sublocality_level_1") {
            inputId = "city";
          }
          else if (addressType === "postal_code") {
            inputId = "postal_code";
          } else if (addressType === "country") {
            inputId = "country";
          }

          const input = this.element.querySelector(`#${inputId}`);
          if (input) {
            input.value = val;
          }
        }
      }
    });

    this.locationInputTarget.value = streetAddress.trim();
  }

  renderAddress(place) {
    if (place.geometry && place.geometry.location ) {
      this.map.setCenter(place.geometry.location);
      this.marker.position = place.geometry.location;
    } else {
      this.marker.position = null;
    }
  }

  uuid() {
    return "xxyxx-xyxyx".replace(
      /[xy]/g,
      function (c) {
        const r = (Math.random() * 16) | 0,
          v = c == "x" ? r : (r & 0x3) | 0x8;
        return v.toString(16);
      }
    );
  }

  formatSavedAddress(address) {

    const formattedAddress = {
      id: address.id,
      addressType: address.address_type,
      buildingType: address.data.building_type,
      address: address.data.address_line,
      apartment: address.data.apt,
      city: address.data.city,
      state: address.data.state,
      postalCode: address.data.postal_code,
      country: address.data.country,
      rooms: address.data.rooms,
      squareArea: address.data.square_area,
      areaUnit: address.data.area_unit,
      stairs: address.data.stairs,
      floor: address.data.floor,
      gateCode: address.data.gate_code,
      elevator: address.data.elevator,
      notes: address.data.notes,
      contacts: address.data.contacts || []
    };

    return formattedAddress;
  }


  saveAddress(event) {
    event.preventDefault();
    const addressData = {
      id: this.selectedAddressId || this.uuid(),
      addressType: this.element.querySelector('#address_type').value,
      buildingType: this.element.querySelector('#building_type').value,
      address: this.locationInputTarget.value,
      apartment: this.element.querySelector('#apartment').value,
      city: this.cityInputTarget.value,
      state: this.stateInputTarget.value,
      postalCode: this.element.querySelector('#postal_code').value,
      country: this.element.querySelector('#country').value,
      rooms: this.element.querySelector('#rooms').value,
      squareArea: this.element.querySelector('#square_area').value,
      areaUnit: this.element.querySelector('#area_unit').value,
      stairs: this.element.querySelector('#stairs').value,
      floor: this.element.querySelector('#floor').value,
      gateCode: this.element.querySelector('#gate_code').value,
      elevator: this.element.querySelector('#elevator').checked,
      notes: this.element.querySelector('#notes').value,
      contacts: this.getContacts()
    };

    if (!addressData.address || !addressData.city || !addressData.state) {
      console.error('Required fields are missing');
      return;
    }

    const existingIndex = this.addressesValue.findIndex((a) => a.id == addressData.id.toString());

    if (existingIndex >= 0) {
      // Update existing address
      this.addressesValue = this.addressesValue.map((address, index) =>
        index === existingIndex ? { ...address, ...addressData } : address
      );
    } else {
      // Add new address
      this.addressesValue = [...this.addressesValue, addressData];
    }

    this.isDataChanged = true;
    //this.updatePositions();
    this.calculateRoutes();
    this.updateHiddenField();
    this.updateAddressList();
    this.calculateAndDisplayRoute();
    this.deselectAddress();
  }

  deselectAddress() {
    if (this.hasAddressListTarget) {
      this.addressListTarget.querySelectorAll("tr").forEach((tr) => {
        tr.classList.remove("bg-orange-200");
      });
    }

    if (this.hasSaveButtonTarget) {
      this.saveButtonTarget.textContent = "Add Address";
    }

    if (this.hasCancelButtonTarget) {
      this.cancelButtonTarget.classList.add("hidden");
    }

    this.selectedAddressId = null;
  }



  isValidForm() {
    let isValid = true;
    if (this.locationInputTarget.value.trim() === "") {
      if (this.hasLocationErrorTarget) {
        this.locationErrorTarget.textContent = "Address is required.";
      }
      isValid = false;
    } else if (this.hasLocationErrorTarget) {
      this.locationErrorTarget.textContent = "";
    }

    if (this.cityInputTarget.value.trim() === "") {
      if (this.hasCityErrorTarget) {
        this.cityErrorTarget.textContent = "City is required.";
      }
      isValid = false;
    } else if (this.hasCityErrorTarget) {
      this.cityErrorTarget.textContent = "";
    }

    if (this.stateInputTarget.value.trim() === "") {
      if (this.hasStateErrorTarget) {
        this.stateErrorTarget.textContent = "State is required.";
      }
      isValid = false;
    } else if (this.hasStateErrorTarget) {
      this.stateErrorTarget.textContent = "";
    }

    return isValid;
  }

  displayErrors() {
  }



  clearForm() {
    const startingPointValue = this.startingPointTarget.value;
    const inputs = this.element.querySelectorAll('input:not([type="hidden"]), select, textarea');
    inputs.forEach(input => {
      console.log(input);
      if (input === this.startingPointTarget) {
        // Skip clearing the starting point input
        return;
      }
      if (input.type === 'checkbox') {
        input.checked = false;
      } else {
        input.value = '';
      }
    });

    if (this.hasContactsContainerTarget) {
      this.contactsContainerTarget.innerHTML = '';
    }

    // Restore the starting point value
    this.startingPointTarget.value = startingPointValue;
    this.clearErrors();
  }

  clearErrors() {
    if (this.hasLocationErrorTarget) {
      this.locationErrorTarget.textContent = "";
    }
    if (this.hasCityErrorTarget) {
      this.cityErrorTarget.textContent = "";
    }
    if (this.hasStateErrorTarget) {
      this.stateErrorTarget.textContent = "";
    }
  }



  async updatePositions() {
    this.addressesValue = this.addressesValue.map((address, index) => ({
      ...address,
      position: index
    }));
    this.isDataChanged = true;

    await this.calculateRoutes();
    this.updateHiddenField();
    this.calculateAndDisplayRoute(); // Add this line to update the route
  }

  async calculateAndDisplayRoute() {
    const currentStartingPoint = this.startingPointTarget.value;
    try {
      const result = await this.googleMapsService.calculateAndDisplayRoute(
        this.addressesValue,
        currentStartingPoint,
        this.businessAddressValue,
        this.measurementUnitValue
      );

      if (result) {
        this.updateTotalDisplay(result.totalDistance, result.totalTime);
      }
    } catch (error) {
      console.error("Error calculating route:", error);
    }
  }

  // ... (keep all remaining existing methods)
  updateMapMarkers() {
    this.googleMapsService.clearMarkers();
    this.addressesValue.forEach((address, index) => {
      const fullAddress = `${address.address}, ${address.city}, ${address.state}, ${address.postalCode}`;
      this.googleMapsService.addMarker(fullAddress, index + 1);
    });
  }


  // Add new method for showing text directions
  showTextDirections(response) {
    if (!this.hasDirectionsPanelTarget) return;

    const directionsPanel = this.directionsPanelTarget;
    directionsPanel.innerHTML = "";
    const route = response.routes[0];

    for (let i = 0; i < route.legs.length; i++) {
      const leg = route.legs[i];
      const legElement = document.createElement("div");
      legElement.classList.add("leg", "mb-4", "p-4", "border", "rounded-lg", "shadow-sm");
      legElement.innerHTML = `<h3 class="text-lg font-semibold mb-2">Leg ${i + 1}: ${leg.start_address} to ${leg.end_address}</h3>`;

      for (let j = 0; j < leg.steps.length; j++) {
        const step = leg.steps[j];
        const stepElement = document.createElement("p");
        stepElement.innerHTML = `${j + 1}. ${step.instructions} (${step.distance.text})`;
        legElement.appendChild(stepElement);
      }

      directionsPanel.appendChild(legElement);
    }
  }

  updateAddressList() {
    //console.log("updateAddressList", this.addressesValue);

    const listBody = document.querySelector("#address-list tbody");
    listBody.innerHTML = "";

    if (this.addressesValue.length === 0) {
      document.querySelector(".no-addresses").style.display = "block";
      console.log("No addresses");
    } else {
      document.querySelector(".no-addresses").style.display = "none";

      this.addressesValue.forEach((address, index) => {
        const clone = document.importNode(
          document.querySelector("#address-template").content,
          true
        );
        const row = clone.querySelector("tr");

        // Set the address ID as a data attribute
        row.setAttribute("data-address-id", address.id);


        let icon = "fa-map-marker-alt";
        let iconColor = "text-blue-500";
        if (address.addressType === "origin") {
          iconColor = "text-green-500";
        } else if (address.addressType === "destination") {
          iconColor = "text-orange-500";
        }
        clone.querySelector(".address-type i").className = `fas ${icon} ${iconColor}`;
        clone.querySelector(".address-type").appendChild(document.createTextNode(` ${address.addressType}`));
        clone.querySelector(".full-address").textContent = `${address.address}, ${address.city}, ${address.state}, ${address.postalCode}`;

        const distanceText = `${address.travelInfo?.formattedDistance || ''} ${address.travelInfo?.formattedDuration || ''}`;
        clone.querySelector(".distance-info").textContent = distanceText || "Calculating...";

        clone.querySelector(".delete-address").setAttribute("data-index", index);
        clone.querySelector(".delete-address").addEventListener("click", (event) => {
          event.preventDefault();
          this.deleteAddress(index);
        });

        row.addEventListener("click", () => {
          this.selectAddress({ currentTarget: row });
        });

        listBody.appendChild(clone);
      });

    }

    this.dispatchAddressUpdate();

  }



  async calculateRoutes() {
    if (this.addressesValue.length < 1) return;

    const origins = [this.businessAddressValue];
    const destinations = this.addressesValue.map(
      (a) => `${a.address}, ${a.city}, ${a.state}, ${a.postalCode}`
    );

    if (this.addressesValue.length > 1) {
      origins.push(
        ...this.addressesValue
          .slice(0, -1)
          .map((a) => `${a.address}, ${a.city}, ${a.state}, ${a.postalCode}`)
      );
    }

    try {
      const response = await this.googleMapsService.callDistanceMatrixAPI(origins, destinations, this.measurementUnitValue);
      this.handleDistanceMatrixResponse(response, "OK");
      this.isDataChanged = false;
    } catch (error) {
      console.error("Error calling Distance Matrix API:", error);
    }
  }



  handleDistanceMatrixResponse(response, status) {
    if (status === "OK") {
      let totalDistance = 0;
      let totalTime = 0;

      const results = response.rows;
      const updatedAddresses = [...this.addressesValue];

      results.forEach((row, index) => {
        const element = row.elements[index];
        if (element.status === "OK") {
          const rawDistance = element.distance.value;
          const rawDuration = element.duration.value;

          const formattedDistance = this.measurementUnitValue === "imperial"
            ? `${(rawDistance * 0.000621371).toFixed(2)} miles`
            : `${(rawDistance / 1000).toFixed(2)} km`;
          const minutes = Math.floor(rawDuration / 60);
          const seconds = rawDuration % 60;
          const formattedDuration = `${minutes} mins ${seconds} secs`;

          totalDistance += this.measurementUnitValue === "imperial"
            ? rawDistance * 0.000621371
            : rawDistance / 1000;
          totalTime += rawDuration;

          let updatedAddress = {
            ...updatedAddresses[index],
            travelInfo: {
              rawDistance,
              rawDuration,
              formattedDistance,
              formattedDuration
            },
          };

          updatedAddresses[index] = updatedAddress;
        }
      });

      this.addressesValue = updatedAddresses;
      this.totalDistance = totalDistance;
      this.totalTime = totalTime;
      this.updateTotalDisplay();

    } else {
      console.error("Error with Distance Matrix API:", status);
    }
  }



  updateTotalDisplay(totalDistance, totalTime) {
    document.getElementById("totalDistance").textContent = totalDistance;
    document.getElementById("totalTime").textContent = totalTime;
    this.updateAddressList();
  }



  deleteAddress(index) {
    this.addressesValue = [
      ...this.addressesValue.slice(0, index),
      ...this.addressesValue.slice(index + 1),
    ];
    this.isDataChanged = true;

    this.updatePositions();
    this.updateAddressList();
    this.calculateAndDisplayRoute();
  }

  addContact(event) {
    event.preventDefault();
    const contactTemplate = this.contactTemplateTarget.content.cloneNode(true);
    const uuid = this.uuid(); // Generate a unique identifier
    contactTemplate.querySelector(".contact").setAttribute("data-contact-id", uuid);
    contactTemplate.querySelector("input[name='contact_id']").value = uuid;
    this.contactsContainerTarget.appendChild(contactTemplate);
  }

  deleteContact(event) {
    event.preventDefault();
    const contactElement = event.target.closest(".contact");
    if (contactElement) {
      contactElement.remove();
      const contactId = contactElement.getAttribute("data-contact-id");
      this.contactsValue = this.contactsValue.filter(contact => contact.id !== contactId);
    }
  }


  populateForm(address) {

    this.element.querySelector('#address_type').value = address.addressType;
    this.element.querySelector('#building_type').value = address.buildingType;
    this.element.querySelector('#address').value = address.address;
    //this.locationInputTarget.value = address.address;
    this.element.querySelector('#apartment').value = address.apartment;
    this.cityInputTarget.value = address.city;
    this.stateInputTarget.value = address.state;
    this.element.querySelector('#postal_code').value = address.postalCode;
    this.element.querySelector('#country').value = address.country;
    this.element.querySelector('#rooms').value = address.rooms;
    this.element.querySelector('#square_area').value = address.squareArea;
    this.element.querySelector('#area_unit').value = address.areaUnit;
    this.element.querySelector('#stairs').value = address.stairs;
    this.element.querySelector('#floor').value = address.floor;
    this.element.querySelector('#gate_code').value = address.gateCode;
    this.element.querySelector('#elevator').checked = address.elevator;
    this.element.querySelector('#notes').value = address.notes;

    if (this.hasContactsContainerTarget) {
      this.contactsContainerTarget.innerHTML = "";
      if (address.contacts && Array.isArray(address.contacts)) {
        address.contacts.forEach(contact => {
          const contactTemplate = this.contactTemplateTarget.content.cloneNode(true);
          contactTemplate.querySelector('.contact').setAttribute("data-contact-id", contact.id);
          contactTemplate.querySelector("input[name='contact_id']").value = contact.id;
          contactTemplate.querySelector("input[placeholder='Contact Name']").value = contact.name;
          contactTemplate.querySelector("input[placeholder='Contact Phone']").value = contact.phone;
          this.contactsContainerTarget.appendChild(contactTemplate);
        });
      }
    }
  }

  // ... (keep all remaining existing methods)
}
